// ===========================================
// Slot Picker Component
// ===========================================
// Reusable time slot selection component for hybrid booking
// Supports cross-day slot display with date grouping.
// Renders unavailable (taken/blocked) slots dimmed but visible, so
// the user can see the full week at a glance.

import { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import { format } from 'date-fns';
import { TimeSlot, formatTimeSlot } from '../../api/hybridBooking.api';
import { formatTime } from '../../api/lessons.api';
import { parseUTCDate } from '../../utils/dateUtils';

// ===========================================
// TYPES
// ===========================================

interface SlotPickerProps {
  /** Time slots to display (both available and unavailable) */
  slots: TimeSlot[] | undefined;
  /** Currently selected slot */
  selectedSlot: TimeSlot | null;
  /** Callback when a slot is selected */
  onSlotSelect: (slot: TimeSlot | null) => void;
  /** Whether slots are loading */
  isLoading: boolean;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Whether to show the selected slot confirmation */
  showConfirmation?: boolean;
  /** Custom confirmation message prefix */
  confirmationPrefix?: string;
  /** Current booking slot (for admin reschedule - highlighted with border) */
  currentSlot?: TimeSlot | null;
}

// ===========================================
// HELPERS
// ===========================================

/** Resolve a tz-stable calendar-day key for a slot */
function resolveDateKey(slot: TimeSlot): string {
  if (slot.dateKey) return slot.dateKey;
  if (slot.date) return new Date(slot.date).toISOString().split('T')[0];
  return 'unknown';
}

// ===========================================
// COMPONENT
// ===========================================

/**
 * SlotPicker - Reusable time slot selection component.
 *
 * Used in:
 * - IndividualBookingTab (parent booking)
 * - CalendarEventDialog (admin reschedule)
 * - StudentBookingsList (admin reschedule)
 *
 * Features:
 * - Paper-tile grid grouped by date with date headings
 * - Unavailable slots rendered dimmed (not hidden) and unclickable
 * - Loading state with skeleton tiles
 * - Empty state when no slots configured at all
 * - Optional confirmation alert showing selected slot
 */
export default function SlotPicker({
  slots,
  selectedSlot,
  onSlotSelect,
  isLoading,
  emptyMessage = 'No available slots for this week.',
  showConfirmation = true,
  confirmationPrefix = 'You are booking for',
  currentSlot = null,
}: SlotPickerProps) {
  const slotsByDate = useMemo(() => {
    if (!slots || slots.length === 0) return new Map<string, TimeSlot[]>();
    const grouped = new Map<string, TimeSlot[]>();
    for (const slot of slots) {
      const key = resolveDateKey(slot);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(slot);
    }
    return grouped;
  }, [slots]);

  if (isLoading) {
    return (
      <Grid container spacing={1}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Grid item xs={3} key={i}>
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!slots || slots.length === 0) {
    return <Alert severity="warning">{emptyMessage}</Alert>;
  }

  const selectedKey = selectedSlot ? resolveDateKey(selectedSlot) : null;

  // The first date-group whose slots are overflow (the clinic booked into the
  // following week). Groups are date-ordered, so overflow groups come last; we
  // render a "Following week" divider once, before this boundary group.
  const groupEntries = Array.from(slotsByDate.entries());
  const firstOverflowKey = groupEntries.find(([, daySlots]) => daySlots[0]?.isOverflow)?.[0] ?? null;

  return (
    <Box>
      {groupEntries.map(([dateStr, daySlots]) => (
        <Box key={dateStr} sx={{ mb: 2 }}>
          {dateStr === firstOverflowKey && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Following week
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.5, display: 'block' }}
          >
            {dateStr !== 'unknown'
              ? format(parseUTCDate(dateStr), 'EEEE, d MMMM')
              : 'Unknown date'}
          </Typography>
          <Grid container spacing={1}>
            {daySlots.map((slot) => {
              const isSelected =
                selectedKey === dateStr &&
                selectedSlot?.startTime === slot.startTime;
              const isCurrent =
                currentSlot &&
                resolveDateKey(currentSlot) === dateStr &&
                currentSlot.startTime === slot.startTime;
              // Mark current slot as taken even if it shows as available (we excluded it from queries)
              const isTaken = !slot.isAvailable || isCurrent;
              return (
                <Grid item xs={3} sm={2} key={`${dateStr}-${slot.startTime}`}>
                  <Paper
                    variant="outlined"
                    onClick={() => {
                      if (!isTaken) onSlotSelect(slot);
                    }}
                    sx={{
                      py: 0.75,
                      px: 0.5,
                      textAlign: 'center',
                      cursor: isTaken ? 'not-allowed' : 'pointer',
                      borderColor: isSelected
                        ? 'primary.main'
                        : isCurrent
                          ? 'warning.main'
                          : 'divider',
                      borderWidth: isCurrent ? 2 : 1,
                      bgcolor: isSelected
                        ? 'primary.main'
                        : isTaken
                          ? 'action.disabledBackground'
                          : 'background.paper',
                      color: isSelected
                        ? 'primary.contrastText'
                        : isTaken
                          ? 'text.disabled'
                          : 'text.primary',
                      opacity: isTaken ? 0.5 : 1,
                      transition: 'all 0.15s',
                      '&:hover': isTaken
                        ? {}
                        : {
                            borderColor: 'primary.main',
                            bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                          },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                      {formatTime(slot.startTime)}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {showConfirmation && selectedSlot && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {confirmationPrefix}{' '}
          {format(parseUTCDate(resolveDateKey(selectedSlot)), 'EEEE, d MMMM yyyy')}{' '}
          at {formatTimeSlot(selectedSlot.startTime, selectedSlot.endTime)}
        </Alert>
      )}
    </Box>
  );
}
